import { ReactNode, useMemo } from "react"
import { InlineSelect } from "@ui/forms/inline"
import { Users, type LucideIcon } from "lucide-react"

import { useFetchTeams } from "@/hooks/api/teams"

import { InputOption } from "../props"

export interface InlineTeamSelectProps {
  label?: ReactNode
  LabelIcon?: LucideIcon
  selectedTeamId: string | null
  setSelectedTeamId: (teamId: string | null) => void
  onSave?: (teamId: string | null) => void | Promise<void>
  isDisabled?: boolean
  fixedLabelWidth?: string
  hideLabel?: boolean
  size?: "sm" | "base" | "lg"
  className?: string
}

export function InlineTeamSelect({
  label,
  selectedTeamId,
  setSelectedTeamId,
  onSave,
  isDisabled,
  fixedLabelWidth,
  hideLabel,
  LabelIcon,
  size,
  className,
}: InlineTeamSelectProps) {
  const { data: teamsData, isLoading } = useFetchTeams()

  const teams = useMemo(() => teamsData?.teams ?? [], [teamsData?.teams])

  const options = useMemo(() => {
    return teams.map((team) => ({
      value: team.id,
      label: team.name,
      color: team.color,
    }))
  }, [teams])

  const renderTeamOption = (option: InputOption & { color?: string | null }) => {
    return (
      <div className="inline-flex min-w-0 items-center gap-2">
        {option?.color && <div className="h-3 w-3 flex-none rounded-full" style={{ backgroundColor: option.color }} />}
        <Users className="h-3.5 w-3.5 flex-none" />
        <span className="truncate">{option?.label}</span>
      </div>
    )
  }

  const renderTeamValue = (option: InputOption | null) => {
    if (!option) {
      return <span className="text-input-placeholder">-</span>
    }
    const team = teams.find((t) => t.id === option.value)
    return (
      <div className="inline-flex min-w-0 items-center gap-2">
        {team?.color && <div className="h-3 w-3 flex-none rounded-full" style={{ backgroundColor: team.color }} />}
        <Users className="h-3.5 w-3.5 flex-none" />
        <span className="truncate">{team?.name || option.label}</span>
      </div>
    )
  }

  return (
    <InlineSelect
      label={label}
      LabelIcon={LabelIcon}
      defaultValue={selectedTeamId}
      placeholder="Assign team..."
      options={options}
      disabled={isLoading || isDisabled}
      onSave={async (value) => {
        setSelectedTeamId(value)
        if (onSave) {
          await onSave(value)
        }
      }}
      hideLoader
      fixedLabelWidth={fixedLabelWidth}
      renderValue={renderTeamValue}
      renderOption={renderTeamOption}
      hideLabel={hideLabel}
      size={size}
      className={className}
    />
  )
}
